"use client";

import AnimateIn from "@/components/AnimateIn";
import Footer from "@/components/Footer";
import { useState } from "react";

const ContactUsPage = () => {
  const [state, setState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("loading");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, message }),
      });

      if (!response.ok) {
        throw new Error("Request failed");
      }

      setState("success");
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setState("error");
    }
  };

  return (
    <div className="bg-white text-gray-900">
      <section
        className="relative border-b border-gray-200"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15, 23, 42, 0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15, 23, 42, 0.06) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <AnimateIn className="stack gap-6">
            <p className="text-[1rem] uppercase tracking-[0.25em] text-gray-500">
              Contact
            </p>
            <h1 className="type-6 font-black uppercase leading-[0.9] type-display text-3xl sm:text-4xl lg:text-6xl">
              Talk To The Thinknado Team
            </h1>
            <p className="text-base sm:text-lg text-gray-700 max-w-3xl">
              Questions, partnership ideas, workshop inquiries, or product
              feedback. Send us a note and we&apos;ll route it to the right person.
            </p>
          </AnimateIn>
        </div>
      </section>

      <section className="py-12 md:py-16 lg:py-20 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <AnimateIn className="stack gap-5">
            <div className="border border-black/15 bg-white p-6 stack gap-3">
              <p className="text-[1rem] uppercase tracking-[0.2em] text-gray-500">
                What To Include
              </p>
              <h2 className="type-4 font-black uppercase leading-tight text-2xl md:text-3xl">
                Help us help you faster
              </h2>
              <ul className="stack gap-2 text-sm md:text-base text-gray-700">
                <li>• A quick summary of your question</li>
                <li>• Any key context or constraints</li>
                <li>• Links or references if relevant</li>
                <li>• Your preferred follow-up method</li>
              </ul>
            </div>

            {/* <div className="border border-black/15 bg-gray-50 p-6 stack gap-2">
              <p className="text-[1rem] uppercase tracking-[0.2em] text-gray-500">
                Direct Email
              </p>
              <p className="text-sm md:text-base text-gray-700">
                Prefer email? Reach us at{" "}
                <a href="mailto:hello@thinknado.com" className="underline">
                  hello@thinknado.com
                </a>
                .
              </p>
            </div> */}
          </AnimateIn>

          <AnimateIn delay="delay-1">
            <form
              onSubmit={handleSubmit}
              className="border border-black/15 p-6 md:p-8 stack gap-5 bg-white"
            >
              <div className="stack gap-2">
                <label htmlFor="name" className="text-[1rem] uppercase tracking-[0.2em] text-gray-600">
                  Name
                </label>
                <input
                  required
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 px-4 border border-black/20 focus:outline-none focus:border-black"
                  placeholder="Your name"
                />
              </div>
              <div className="stack gap-2">
                <label htmlFor="email" className="text-[1rem] uppercase tracking-[0.2em] text-gray-600">
                  Email
                </label>
                <input
                  required
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 px-4 border border-black/20 focus:outline-none focus:border-black"
                  placeholder="you@company.com"
                />
              </div>
              <div className="stack gap-2">
                <label
                  htmlFor="message"
                  className="text-[1rem] uppercase tracking-[0.2em] text-gray-600"
                >
                  Message
                </label>
                <textarea
                  required
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-40 p-4 border border-black/20 focus:outline-none focus:border-black resize-y"
                  placeholder="What are you working on, and how can we help?"
                />
              </div>

              {state === "success" && (
                <p className="text-sm md:text-base font-semibold text-gray-900">
                  Message sent. We&apos;ll be in touch soon.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm md:text-base font-semibold text-red-700">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}

              <button
                disabled={state === "loading"}
                type="submit"
                className="w-full border border-black bg-black text-white px-6 py-3 text-sm font-black uppercase tracking-[0.14em] disabled:opacity-60 hover:bg-white hover:text-black transition"
              >
                {state === "loading" ? "Sending..." : "Send Message"}
              </button>
            </form>
          </AnimateIn>
        </div>
      </section>

      {/* <section className="py-12 md:py-16 bg-white text-black">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <AnimateIn className="border border-black/20 p-6 md:p-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="stack gap-3 max-w-3xl">
              <p className="text-[1rem] uppercase tracking-[0.25em] text-black/70">
                Also Looking For
              </p>
              <h2 className="type-4 font-black uppercase leading-[0.95] text-2xl md:text-4xl">
                Workshops or team sessions?
              </h2>
              <p className="text-sm md:text-base text-black/80">
                Tell us your team size, timeline, and outcome you want.
              </p>
            </div>
            <a
              href="mailto:hello@thinknado.com?subject=Workshop%20Inquiry"
              className="w-full lg:w-auto text-center border border-black bg-white text-black px-6 py-3 text-sm font-black uppercase tracking-[0.14em] hover:bg-black hover:text-white transition"
            >
              Email Workshop Team
            </a>
          </AnimateIn>
        </div>
      </section> */}

      <Footer />
    </div>
  );
};

export default ContactUsPage;
